// Formatação de data/hora do sistema.
//
// Princípio: horário absoluto "de parede" (wall-clock), SEM fuso. A API grava e devolve o valor
// literal (ex: "2026-06-12T12:00:00", sem 'Z'/offset), e a exibição mostra exatamente esse valor —
// 12:00 é 12:00 para qualquer pessoa, independente do fuso de quem está vendo. Sem conversão de GMT.

// Interpreta o valor como horário literal, montando a partir das partes para nunca aplicar fuso
// (strings só-data em JS seriam lidas como UTC; aqui isso é evitado). Marcadores de fuso são ignorados.
const parseLiteral = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  const stripped = value.replace(/[zZ]$/, '').replace(/[+-]\d{2}:?\d{2}$/, '');
  const match = stripped.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/
  );

  if (!match) {
    const fallback = new Date(stripped);
    return isNaN(fallback.getTime()) ? null : fallback;
  }

  const [, year, month, day, hour = '0', minute = '0', second = '0'] = match;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second)
  );
  return isNaN(date.getTime()) ? null : date;
};

const pad = (n: number): string => String(n).padStart(2, '0');

export const formatDateTime = (value: string | Date | null | undefined): string => {
  const d = parseLiteral(value);
  if (!d) return '';
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export const formatDate = (value: string | Date | null | undefined): string => {
  const d = parseLiteral(value);
  if (!d) return '';
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

export const formatTime = (value: string | Date | null | undefined): string => {
  const d = parseLiteral(value);
  if (!d) return '';
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Data local yyyy-MM-dd a partir das partes locais do Date — sem passar por toISOString,
// que converte para UTC e desloca o dia à noite em fusos negativos (ex.: UTC-3).
export const toLocalDateStr = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Data local yyyy-MM-dd deslocada em N dias — defaults de filtros de período
export const shiftDaysLocalDateStr = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toLocalDateStr(d);
};

// Data local yyyy-MM-dd deslocada em N meses (negativo = passado) — defaults de filtros de período
export const shiftMonthsLocalDateStr = (months: number): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return toLocalDateStr(d);
};
